import {
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { LoadingService } from "../../../core/loader/loading.service";
import { GenericContentDirective } from "../../directives/content.directive";
import { DataTableComponent } from "../data-table/data-table.component";
import { DynamicFormComponent } from "../dynamic-form/dynamic-form.component";
import { PropertyListComponent } from "../property-list/property-list.component";
import { GenericModalData } from "./generic-modal.type";

interface GenericModalComponentConfig {
  buttons: {
    submit: {
      name: string;
    };
    cancel: {
      name: string;
    };
  };
}

@Component({
  selector: "app-generic-modal",
  templateUrl: "./generic-modal.component.html",
  styleUrls: ["./generic-modal.component.css"],
})
export class GenericModalComponent<T = any> implements OnInit {
  @ViewChild(GenericContentDirective, { static: true })
  content: GenericContentDirective;
  componentInstance: T;
  config: GenericModalComponentConfig;

  loading$ = this.loader.loading$;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: GenericModalData,
    public dialogRef: MatDialogRef<GenericModalComponent>,
    private loader: LoadingService,
    private componentFactoryResolver: ComponentFactoryResolver,
  ) {}

  @HostListener("window:keyup.esc") onKeyUp(): void {
    this.close(false);
  }

  ngOnInit(): void {
    this.loadContent();
  }

  /** Load the modal */
  loadContent(): void {
    const contentComponent = this.data.component;

    if (!contentComponent) {
      return;
    }

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(contentComponent.clazz);
    const viewContainerRef = this.content.viewContainerRef;
    viewContainerRef.clear();
    const componentRef = viewContainerRef.createComponent<T>(componentFactory);
    this.componentInstance = componentRef.instance;

    const instance = this.componentInstance;

    this.setDefaultContent();

    // Set component inputs (each provided @Input)
    if (contentComponent.inputs) {
      Object.entries(contentComponent.inputs).forEach(([key, value]) => {
        instance[key] = value;
      });
    }

    // Replace the default behavior of `onSubmit` action
    if (contentComponent.onSubmit) {
      this.onSubmit = () => {
        instance[contentComponent.onSubmit].call(instance);
      };
    }

    // Subscribe to each provided event of the component
    if (contentComponent.events?.length) {
      contentComponent.events.forEach((currentEvent) => {
        const eventEmitter = instance[currentEvent.eventName] as EventEmitter<unknown>;
        eventEmitter.subscribe((event) => {
          // Callback/handler to be executed
          currentEvent.handler(instance, event);
        });
      });
    }
  }

  /** Execute the default behavior on submit data.
   *
   * It can be overridden by providing a value in `data.component.onSubmit`
   * or if the component has a method called `onSubmit`*/
  onSubmit(event: Event): void {
    this.close(event);
  }

  onDisable(): boolean {
    if (this.componentInstance instanceof DynamicFormComponent) {
      return this.componentInstance.isDisabled();
    }

    if (this.componentInstance instanceof DataTableComponent) {
      return !this.componentInstance.config.selection.selectedRows.length;
    }

    if (this.componentInstance instanceof PropertyListComponent) {
      return this.componentInstance.form.invalid;
    }

    return true;
  }

  private setDefaultContent(): void {
    this.config = {
      buttons: {
        submit: { name: "Submit" },
        cancel: { name: "Cancel" },
      },
    };

    if (this.componentInstance instanceof DynamicFormComponent) {
      this.config.buttons.submit.name = "Submit";
    }

    if (this.componentInstance instanceof DataTableComponent) {
      this.config.buttons.submit.name = "Assign";
    }

    if (this.componentInstance instanceof PropertyListComponent) {
      this.config.buttons.submit.name = "Update";
    }
  }

  /** Close the modal */
  private close(result?: any): void {
    this.dialogRef.close(result);
  }
}
